import React from "react";

/**
 * Text input for cells inside VirtualTable (react-window).
 *
 * Keystrokes live in local state and are committed via `onCommit` on blur —
 * AND on unmount. The unmount commit is the whole point: react-window
 * unmounts rows that scroll past the overscan window, and an unmounted
 * input never fires blur, so a plain defaultValue/onBlur input silently
 * drops whatever was typed. (That showed up as "cell goes empty after I
 * scroll to reach the far columns".)
 *
 * The pending edit is tracked on an instance field, not in state: the
 * blur commit re-keys this component (parent keys it with the committed
 * value), and componentWillUnmount must see the flag synchronously or the
 * same value gets committed twice. The field also gates the commit so
 * untouched cells never write back — excel cells can hold numbers or
 * Dates, and committing them would stringify them just because the row
 * scrolled by.
 *
 * The parent key (`rowKey-colKey-value`) also means an external change to
 * the row remounts the cell and re-seeds local state. Don't drop the
 * value from that key.
 *
 * Props:
 *  defaultValue  committed value from the row object
 *  onCommit      (value) => void — write the edited value into state
 *  name, className  passed through to the <input>
 */
class EditableCell extends React.Component {
  constructor(props) {
    super(props);
    this.state = { value: props.defaultValue ?? "" };
    this.pendingValue = null; // non-null only while an edit is uncommitted
  }

  commitPending() {
    if (this.pendingValue !== null) {
      const value = this.pendingValue;
      this.pendingValue = null;
      this.props.onCommit(value);
    }
  }

  componentWillUnmount() {
    this.commitPending();
  }

  render() {
    const { name, className } = this.props;
    return (
      <input
        name={name}
        className={className}
        value={this.state.value ?? ""}
        onChange={(e) => {
          this.pendingValue = e.target.value;
          this.setState({ value: e.target.value });
        }}
        onBlur={() => this.commitPending()}
      />
    );
  }
}

export default EditableCell;
